import { type fabric } from 'fabric';
import { type Constructor } from '../types';
import type WhiteboardController from '../controllers/WhiteboardController';
import {
  type MountedObject,
  type CanvaSXObject,
  type MountedObjects,
} from '@lib/canvasx/canvasx';
import { type IEvent, type IPoint } from 'fabric/fabric-impl';
import { renderTooltip } from './WhiteboardTooltip';
import { type ObjectHoverEvent } from '../external-types';
import { getBoundingBoxFromLocalRect } from '../utils/geometry';

interface CanvaSXBehaviors extends CanvaSXObject {
  __hasCanvaSXBehaviors: boolean;
  controller: WhiteboardController;
  mountedObjects: MountedObjects;
  hoveredKey?: InteractiveKey;
  showTooltip(key: string, mountedObject: MountedObject): void;
  canFire: boolean;
  requiresPriorSelection: boolean;
  selected: boolean;
  findKeyForEvent: (
    event: IEvent<MouseEvent>,
    pointer?: IPoint
  ) => string | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function CanvaSXBehaviors(Base: Constructor<fabric.Object>): any {
  return class extends Base implements CanvaSXBehaviors {
    __hasCanvaSXBehaviors = true;
    controller: WhiteboardController;
    mountedObjects: MountedObjects = {};
    hoveredKey?: InteractiveKey;
    mouseWasDown = false;
    // This requires that objects be selected before onClick can fire. False by
    // default
    requiresPriorSelection: boolean;
    selected = false;

    hoverTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};
    hoverTooltips: Record<string, ReturnType<typeof renderTooltip>> = {};

    constructor(options) {
      super(options);
      this.requiresPriorSelection ||= false;

      this.on('mousemove', this.onMouseMove);

      this.on('mousedown:before', this.onMouseDownBefore);

      this.on('mousedown', this.onMouseDown);

      this.on('mouseup', this.onMouseUp);

      this.on('mouseout', ev => {
        if (this.hoveredKey) {
          this.fire('hover:out', {
            event: ev,
            key: this.hoveredKey,
            mountedObject: this.mountedObjects[this.hoveredKey],
          });
        }

        this.hoveredKey = undefined;
        this.canvas.setCursor('default');
        this.controller.renderManager.refreshObject(this);
        this.canvas.requestRenderAll();
      });

      this.on('hover:over', ({ event, mountedObject }) => {
        if (mountedObject?.onMouseOver) {
          mountedObject.onMouseOver(event);
        }
      });

      this.on('hover:move', ({ event, mountedObject }) => {
        if (mountedObject?.onMouseMove) {
          mountedObject.onMouseMove(event);
        }
      });

      this.on('hover:out', ({ event, mountedObject }) => {
        if (mountedObject?.onMouseOut) {
          mountedObject.onMouseOut(event);
        }
      });

      // Handle hovering over tooltips
      this.on('hover:over', this.hoverOverTooltip);
      this.on('hover:out', ({ key }) => this.hideTooltip(key));
      this.on('mouseout', () => {
        Object.keys(this.hoverTooltips).forEach(key => this.hideTooltip(key));
      });
    }

    onMouseMove = (event: IEvent<MouseEvent>, pointer?: IPoint): void => {
      // Avoid showing mounted object cursors when object is locked
      if (this.locked) {
        this.hoverCursor = 'default';
        return;
      }

      const hoveredKey = this.findKeyForEvent(event, pointer);

      const mountedObject = hoveredKey
        ? this.mountedObjects[hoveredKey]
        : undefined;

      if (mountedObject?.cursor) {
        this.hoverCursor = mountedObject.cursor;
      } else if (mountedObject?.onClick) {
        this.hoverCursor = 'pointer';
      } else {
        this.hoverCursor = undefined;
      }

      // Turn off mouse down, we don't want to click if the user dragged
      this.mouseWasDown = false;

      // Re-render everything if hovered key changed
      if (hoveredKey !== this.hoveredKey) {
        if (hoveredKey) {
          this.fire('hover:over', { event, key: hoveredKey, mountedObject });
        }

        if (this.hoveredKey) {
          this.fire('hover:out', {
            event,
            key: this.hoveredKey,
            mountedObject: this.mountedObjects[this.hoveredKey],
          });
        }

        this.controller.renderManager.refreshObject(this);
        this.canvas.requestRenderAll();
      }
      this.hoveredKey = hoveredKey;

      if (hoveredKey) {
        this.fire('hover:move', { event, key: hoveredKey, mountedObject });
      }
    };

    onMouseDownBefore(): void {
      const activeSelection = this.canvas.getActiveObject();
      this.selected = activeSelection === this;
    }

    onMouseDown = (): void => {
      this.mouseWasDown = true;
    };

    onMouseUp = (event: IEvent<MouseEvent>, pointer?: IPoint): void => {
      const key = this.findKeyForEvent(event, pointer);

      if (key && this.canFire) {
        const mountedObject = this.mountedObjects[key];

        if (typeof mountedObject.onClick === 'function') {
          mountedObject.onClick(event);
        }
      }

      this.mouseWasDown = false;
    };

    findKeyForEvent(event: IEvent<MouseEvent>, pointer?: IPoint) {
      pointer ||= this.getLocalPointer(event.e);

      // Find the pointer x/y relative to the object (need to adjust for object scale)
      const px = pointer.x;
      const py = pointer.y;

      const objectKey = Object.keys(this.mountedObjects).find(key => {
        const mountedObject = this.mountedObjects[key];
        const x = mountedObject.x;
        const y = mountedObject.y;
        const w = mountedObject.width;
        const h = mountedObject.height;

        return px >= x && px <= x + w && py >= y && py <= y + h;
      });

      if (objectKey) {
        return this.mountedObjects[objectKey].hoveredKey || objectKey;
      }
    }

    get canFire() {
      if (this.requiresPriorSelection && !this.selected) {
        return false;
      }
      return this.mouseWasDown;
    }

    private readonly hoverOverTooltip = ({
      key,
      mountedObject,
    }: ObjectHoverEvent) => {
      this.hoverTimeouts[key] = setTimeout(() => {
        this.showTooltip(key, mountedObject);
      }, 200);
    };

    showTooltip(key: string, mountedObject: MountedObject): void {
      if (!mountedObject.tooltip) {
        return;
      }

      let content: React.ReactNode;
      if (typeof mountedObject.tooltip === 'function') {
        content = mountedObject.tooltip(key);
      } else {
        content = mountedObject.tooltip;
      }

      const rect = getBoundingBoxFromLocalRect({
        rect: {
          left: mountedObject.x,
          top: mountedObject.y,
          width: mountedObject.width,
          height: mountedObject.height,
        },
        object: this,
      });

      const tooltipCoords = this.canvas.rectToWindowCoords(rect);

      if (!tooltipCoords) return;

      this.hoverTooltips[key] = renderTooltip(
        content,
        tooltipCoords,
        this.canvas
      );
    }

    hideTooltip(key: string) {
      if (this.hoverTimeouts[key]) {
        clearTimeout(this.hoverTimeouts[key]);
        delete this.hoverTimeouts[key];
      }

      if (this.hoverTooltips[key]) {
        this.hoverTooltips[key]();
        delete this.hoverTooltips[key];
      }
    }
  };
}

export default CanvaSXBehaviors;
